import React, { useEffect, useState } from 'react'
import DayPicker from 'react-day-picker'
import 'react-day-picker/lib/style.css'
import MomentLocaleUtils from 'react-day-picker/moment'
import { isMobile } from 'react-device-detect'
import styled from 'styled-components'
import prevIcon from 'img/circle-arrow-left-solid'
import nextIcon from 'img/circle-arrow-right-solid'
import time from 'i18n/time'
import resCn from 'lib/responsive_classes'

const StyledDayPicker = styled(DayPicker).attrs({
  className: resCn({
    wide: 'justify-content-start',
    slim: 'justify-content-center',
    all: 'd-flex'
  })
})`
  .DayPicker-wrapper {
    background-color: white;
    border: 1px solid var(--bs-primary);
    border-radius: 15px;
    padding: 0;
  }

  .DayPicker-Caption {
    font-size: 1em;
    color: var(--bs-white);
    padding: 0.35em;

    > div {
      padding: 0.3em;
      background: var(--bs-primary);
      border-radius: var(--bs-border-radius-2xl);
    }
  }

  .DayPicker-NavButton {
    top: 0.65em;
    right: 0.75em;
    width: 2em;
  }

  .DayPicker-NavBar {
    color: var(--bs-primary);
  }

  .DayPicker-NavButton--prev {
    width: 1.5em;
    height: 1.5em;
    left: 0.75em;
    background-color: var(--bs-white);
    background-image: none;

    -webkit-mask: url(${prevIcon}) no-repeat center;
    -webkit-mask-size: contain;
    mask: url(${prevIcon}) no-repeat center;
    mask-size: contain;
  }

  .DayPicker-NavButton--next {
    width: 1.5em;
    height: 1.5em;
    right: 0.75em;
    background-color: var(--bs-white);
    background-image: none;

    -webkit-mask: url(${nextIcon}) no-repeat center;
    -webkit-mask-size: contain;
    mask: url(${nextIcon}) no-repeat center;
    mask-size: contain;
  }

  .DayPicker-Month {
    margin: 0;
  }

  .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):hover {
    background-color: red;
  }

  .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {
    background-color: var(--bs-primary);
  }

  .DayPicker-Weekday {
    color: var(--bs-primary);
  }

  .DayPicker-Week:last-of-type {
    .DayPicker-Day:first-of-type {
      border: 0;
      border-bottom-left-radius: var(--bs-border-radius-xl);
    }

    .DayPicker-Day:last-of-type {
      border: 0;
      border-bottom-right-radius: var(--bs-border-radius-xl);
    }
  }

  .DayPicker-Day {
    ${({ isMobile: mobile }) => (
      mobile
        ? 'padding: 0.35em 0.5em;'
        : 'padding: 0.6em 0.7em;'
    )}

    font-size: 1.15em;
    font-weight: 700;
    border: 1px solid #e9ecef;
    border-radius: 0;
  }

  .DayPicker-Caption > div {
    text-align: center;
  }

  .DayPicker-Day--past {
    color: #cfcfcf;
    background: #f1f1f1;
  }

  .DayPicker-Day--today:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside) {
    color: var(--bs-dark);
  }
`

const parseDate = (value, fallback = new Date()) => {
  if (!value) return fallback

  const parsedDate = new Date(value)

  return Number.isNaN(parsedDate.getTime())
    ? fallback
    : parsedDate
}

const CalendarPicker = ({
  initialDay,
  daysAvailable,
  onDaySelected,
  onMonthChange,
  locale,
  resSize,
  fromMonth,
  stepStatus
}) => {
  const initialSelectedDay = parseDate(initialDay)
  const firstAvailableMonth = parseDate(fromMonth)

  const [selectedDay, setSelectedDay] = useState(initialSelectedDay)
  const [visibleMonth, setVisibleMonth] = useState(initialSelectedDay)

  useEffect(() => {
    if (stepStatus !== 'active') return

    const nextSelectedDay = parseDate(initialDay)

    setSelectedDay(nextSelectedDay)
    setVisibleMonth(nextSelectedDay)
  }, [stepStatus, initialDay])

  const isDayDisabled = day => {
    if (!daysAvailable) return false

    const localDate = time
      .tz(day, 'Europe/Madrid')
      .format('YYYY-MM-DD')

    return !daysAvailable.some(availableDay => (
      localDate === availableDay
    ))
  }

  const handleDayClick = (day, { disabled }) => {
    if (disabled) return

    setSelectedDay(day)
    setVisibleMonth(day)
    onDaySelected?.(day)
  }

  const handleMonthChange = month => {
    setVisibleMonth(month)
    onMonthChange?.(month)
  }

  const modifiers = {
    today: new Date(),
    past: {
      from: new Date(1900, 0, 1),
      to: time().subtract(1, 'day').toDate()
    }
  }

  return (
    <StyledDayPicker
      month={visibleMonth}
      selectedDays={selectedDay}
      onDayClick={handleDayClick}
      onMonthChange={handleMonthChange}
      disabledDays={[
        isDayDisabled,
        { before: initialSelectedDay }
      ]}
      fromMonth={firstAvailableMonth}
      showOutsideDays
      localeUtils={MomentLocaleUtils}
      locale={locale}
      firstDayOfWeek={1}
      isMobile={isMobile}
      modifiers={modifiers}
    />
  )
}

export default CalendarPicker
